/**
 * Unified Column Configuration for Tree View
 * 
 * This module defines the single source of truth for column layout across
 * ProjectRow, SequenceRow, TaskRow, and the sticky header to ensure pixel-perfect alignment.
 * Uses CSS Grid with CSS custom properties for guaranteed synchronization.
 */

import type { CSSProperties } from 'react';

export const TREE_COLUMNS = {
  thumbnail: { width: 'w-24', gridWidth: '6rem', cssVar: '--tree-col-thumbnail' },      
  expand: { width: 'w-4', gridWidth: '1rem', cssVar: '--tree-col-expand' },          
  id: { width: 'w-24', gridWidth: '6rem', cssVar: '--tree-col-id' },             
  name: { width: 'flex-1', gridWidth: '1fr', cssVar: '--tree-col-name', minWidth: '10rem' },
  leadArtist: { width: 'w-12', gridWidth: '3rem', cssVar: '--tree-col-lead' },     
  team: { width: 'w-16', gridWidth: '4rem', cssVar: '--tree-col-team' },           
  projectStatus: { width: 'w-40', gridWidth: '10rem', cssVar: '--tree-col-project-status' }, 
  status: { width: 'w-40', gridWidth: '10rem', cssVar: '--tree-col-status' },        
  timing: { width: 'w-48', gridWidth: '12rem', cssVar: '--tree-col-timing' },        
  // One timer control (24px), three row actions (72px), and three gaps (12px).
  actions: { width: 'w-28', gridWidth: '7rem', cssVar: '--tree-col-actions' },
  budget: { width: 'w-48', gridWidth: '12rem', cssVar: '--tree-col-budget' },
} as const;

/**
 * CSS Grid template string using CSS custom properties
 * Ensures header and rows use identical column widths
 */
export const TREE_GRID_TEMPLATE = 
  `var(${TREE_COLUMNS.thumbnail.cssVar}) ` +
  `var(${TREE_COLUMNS.expand.cssVar}) ` +
  `var(${TREE_COLUMNS.id.cssVar}) ` +
  `var(${TREE_COLUMNS.name.cssVar}) ` +
  `var(${TREE_COLUMNS.leadArtist.cssVar}) ` +
  `var(${TREE_COLUMNS.team.cssVar}) ` +
  `var(${TREE_COLUMNS.projectStatus.cssVar}) ` +
  `var(${TREE_COLUMNS.status.cssVar}) ` +
  `var(${TREE_COLUMNS.timing.cssVar}) ` +
  `var(${TREE_COLUMNS.actions.cssVar})`;

export const TREE_GRID_TEMPLATE_WITH_BUDGET = 
  TREE_GRID_TEMPLATE + ` var(${TREE_COLUMNS.budget.cssVar})`;

/**
 * Generate CSS custom properties object for tree column widths
 * Apply this to a parent container to make grid template work
 */
export function getTreeColumnStyles(showBudget = false): CSSProperties {
  const base: Record<string, string> = {
    [TREE_COLUMNS.thumbnail.cssVar]: TREE_COLUMNS.thumbnail.gridWidth,
    [TREE_COLUMNS.expand.cssVar]: TREE_COLUMNS.expand.gridWidth,
    [TREE_COLUMNS.id.cssVar]: TREE_COLUMNS.id.gridWidth,
    [TREE_COLUMNS.name.cssVar]: TREE_COLUMNS.name.gridWidth,
    [TREE_COLUMNS.leadArtist.cssVar]: TREE_COLUMNS.leadArtist.gridWidth,
    [TREE_COLUMNS.team.cssVar]: TREE_COLUMNS.team.gridWidth,
    [TREE_COLUMNS.projectStatus.cssVar]: TREE_COLUMNS.projectStatus.gridWidth,
    [TREE_COLUMNS.status.cssVar]: TREE_COLUMNS.status.gridWidth,
    [TREE_COLUMNS.timing.cssVar]: TREE_COLUMNS.timing.gridWidth,
    [TREE_COLUMNS.actions.cssVar]: TREE_COLUMNS.actions.gridWidth,
  };
  if (showBudget) {
    base[TREE_COLUMNS.budget.cssVar] = TREE_COLUMNS.budget.gridWidth;
  }
  return base as CSSProperties;
}

function parseRemToPixels(gridWidth: string): number {
  const match = gridWidth.match(/^(\d+(?:\.\d+)?)rem$/);
  return match ? parseFloat(match[1]) * 16 : 0;
}

const fixedColumnsBase = (
  parseRemToPixels(TREE_COLUMNS.thumbnail.gridWidth) +
  parseRemToPixels(TREE_COLUMNS.expand.gridWidth) +
  parseRemToPixels(TREE_COLUMNS.id.gridWidth) +
  parseRemToPixels(TREE_COLUMNS.leadArtist.gridWidth) +
  parseRemToPixels(TREE_COLUMNS.team.gridWidth) +
  parseRemToPixels(TREE_COLUMNS.projectStatus.gridWidth) +
  parseRemToPixels(TREE_COLUMNS.status.gridWidth) +
  parseRemToPixels(TREE_COLUMNS.timing.gridWidth) +
  parseRemToPixels(TREE_COLUMNS.actions.gridWidth) +
  parseRemToPixels(TREE_COLUMNS.name.minWidth || '10rem')
);

const budgetColumnPixels = parseRemToPixels(TREE_COLUMNS.budget.gridWidth);

export const TREE_MIN_WIDTH = `${fixedColumnsBase}px`;
export const TREE_MIN_WIDTH_WITH_BUDGET = `${fixedColumnsBase + budgetColumnPixels}px`;

/**
 * Width in pixels of the three "sticky" tree columns that remain visible in
 * timeline mode (Task #107): thumbnail + expand chevron + id.
 * Used by `TimelineLayer` to align the overlay's left edge with the freed
 * right-hand grid area so it never overlaps the sticky column block.
 * Derived from {@link TREE_COLUMNS} so it can never drift from the actual
 * grid column widths.
 */
export const TREE_STICKY_COL_PX =
  parseRemToPixels(TREE_COLUMNS.thumbnail.gridWidth) +
  parseRemToPixels(TREE_COLUMNS.expand.gridWidth) +
  parseRemToPixels(TREE_COLUMNS.id.gridWidth);

/**
 * Helper to get consistent column class names (legacy - prefer CSS Grid)
 */
export function getColumnClass(columnName: keyof typeof TREE_COLUMNS, additionalClasses = '') {
  const column = TREE_COLUMNS[columnName];
  return `${column.width} ${additionalClasses}`.trim();
}
